import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EmployeeSection = ({ data, stats }) => {
  const [rolesData, setRolesData] = useState([])
  const [employeeData, setEmployeeData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch both roles and employee data using new route or data prop
  useEffect(() => {
    if (data && (data.employees || data.roles)) {
      setEmployeeData(data.employees || [])
      setRolesData(data.roles || [])
      setLoading(false)
    } else {
      fetchAllData()
    }
  }, [data])

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      setLoading(true)
      setError(null)
      
      const response = await axios.get(`${API_URL}/api/employee-report/summary?period=yearly`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })
      
      if (response.data.status === "success" && response.data.data) {
        setRolesData(response.data.data.roles || [])
        setEmployeeData(response.data.data.employees || [])
      } else {
        setError('Failed to load employee report data')
      }
    } catch (error) {
      //console.error('Error fetching data:', error)
      setError('Failed to connect to the server')
    } finally {
      setLoading(false)
    }
  }

  // Helper function to get employee full name with better fallback logic
  const getEmployeeFullName = (emp) => {
    if (!emp) return "Unknown Employee"
    
    // Check for fullName first
    if (emp.fullName && emp.fullName.trim()) return emp.fullName.trim()
    
    // Try firstName + lastName combination
    if (emp.firstName && emp.lastName) {
      return `${emp.firstName.trim()} ${emp.lastName.trim()}`
    }
    
    // Fall back to individual names
    if (emp.firstName && emp.firstName.trim()) return emp.firstName.trim()
    if (emp.lastName && emp.lastName.trim()) return emp.lastName.trim()
    if (emp.name && emp.name.trim()) return emp.name.trim()
    
    return "Unknown Employee"
  }

  // Helper function to get employee email with better priority
  const getEmployeeEmail = (emp) => {
    if (!emp) return "No email"
    return emp.companyemail || emp.personalemail || emp.email || "No email"
  }

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return "Invalid Date"
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    } catch (error) {
      return "Invalid Date"
    }
  }

  // Get initials for avatar
  const getInitials = (emp) => {
    const fullName = getEmployeeFullName(emp)
    if (fullName === "Unknown Employee") return "UE"
    
    const names = fullName.split(' ').filter(name => name.length > 0)
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase()
    }
    return names[0] ? names[0][0].toUpperCase() : "E"
  }

  // Enhanced function to find employee status
  const getEmployeeStatus = (roleRecord) => {
    if (!roleRecord) return 'Unknown'
    

    
    // First, try to find matching employee record by employee_id
    const matchingEmployee = employeeData.find(emp => {
      return emp.employee_id === roleRecord.employee_id || 
             emp.id === roleRecord.employee_id ||
             emp.employee_id === roleRecord.id
    })
    

    
    // Helper function to check if a value is a valid status
    const isValidStatus = (value) => {
      if (!value) return false
      if (typeof value === 'boolean') return true
      if (typeof value === 'string') return value.trim() !== ''
      return false
    }
    
    // Check all possible status fields in priority order
    // 1. Check employee data status fields first
    if (matchingEmployee) {
      // Check common status field names
      const statusFields = [
        'status', 'employeeStatus', 'workStatus', 'currentStatus', 
        'employee_status', 'work_status', 'current_status',
        'employmentStatus', 'employment_status'
      ]
      
      for (const field of statusFields) {
        if (isValidStatus(matchingEmployee[field])) {
          const status = matchingEmployee[field]
          return typeof status === 'string' ? status.trim() : (status ? 'Active' : 'Inactive')
        }
      }
      
      // Check for special status indicators
      if (matchingEmployee.resignationDate || matchingEmployee.resignation_date || matchingEmployee.lastWorkingDay) {
        return 'Resigned'
      }
      if (matchingEmployee.terminationDate || matchingEmployee.termination_date || matchingEmployee.terminated === true) {
        return 'Terminated'
      }
      if (matchingEmployee.deceased === true) {
        return 'Deceased'
      }
    }
    
    // 2. Check role record status fields
    const roleStatusFields = [
      'status', 'employeeStatus', 'workStatus', 'currentStatus',
      'employee_status', 'work_status', 'current_status',
      'employmentStatus', 'employment_status'
    ]
    
    for (const field of roleStatusFields) {
      if (isValidStatus(roleRecord[field])) {
        const status = roleRecord[field]
        return typeof status === 'string' ? status.trim() : (status ? 'Active' : 'Inactive')
      }
    }
    
    // Check for special status indicators in role record
    if (roleRecord.resignationDate || roleRecord.resignation_date || roleRecord.lastWorkingDay) {
      return 'Resigned'
    }
    if (roleRecord.terminationDate || roleRecord.termination_date || roleRecord.terminated === true) {
      return 'Terminated'
    }
    if (roleRecord.deceased === true) {
      return 'Deceased'
    }
    
    // 3. If we have employee data but no explicit status, assume Active
    if (matchingEmployee) {
      return 'Active'
    }
    
    // 4. If no employee data found, check if role record exists (means pending)
    return 'Pending'
  }

  const getStatusChip = (status) => {
    if (!status) {
      return (
        <Chip
          label="Unknown"
          size="small"
          sx={{ bgcolor: "#e2e3e5", color: "#6c757d", fontWeight: 500, fontSize: "0.75rem" }}
        />
      )
    }

    const statusLower = status.toLowerCase()
    const statusConfig = {
      active: { color: "#28a745", bgcolor: "#d4edda", label: "Active" },
      inactive: {  color: "#dc3545", bgcolor: "#f8d7da",label: "Inactive" },
      pending: { color: "#ffc107", bgcolor: "#fff3cd", label: "Pending" },
      approved: { color: "#28a745", bgcolor: "#d4edda", label: "Approved" },
      rejected: { color: "#dc3545", bgcolor: "#f8d7da", label: "Rejected" },
      resigned: { color: "#fd7e14", bgcolor: "#ffeaa7", label: "Resigned" },
      terminated: { color: "#dc3545", bgcolor: "#f8d7da", label: "Terminated" },
      deceased: { color: "#343a40", bgcolor: "#f8f9fa", label: "Deceased" },
      unknown: { color: "#6c757d", bgcolor: "#e2e3e5", label: "Unknown" },
      onleave: { color: "#17a2b8", bgcolor: "#d1ecf1", label: "On Leave" },
      suspended: { color: "#e83e8c", bgcolor: "#f5c6cb", label: "Suspended" },
    }

    const config = statusConfig[statusLower] || { color: "#6c757d", bgcolor: "#e2e3e5", label: status }
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          color: config.color,
          bgcolor: config.bgcolor,
          fontWeight: 500,
          fontSize: "0.75rem",
        }}
      />
    )
  }

  // Prepare data for charts using both roles and employee data
  const prepareChartData = () => {
    if (!Array.isArray(rolesData) || rolesData.length === 0) {
      return {
        departmentData: [],
        statusData: []
      }
    }

    // Department distribution from roles data - EXCLUDE Unknown departments
    const departmentCount = {}
    rolesData.forEach(role => {
      const dept = role.department || 'Unknown'
      // Only count departments that are not "Unknown"
      if (dept && dept.toLowerCase() !== 'unknown' && dept.trim() !== '') {
        departmentCount[dept] = (departmentCount[dept] || 0) + 1
      }
    })

    // Calculate total for percentage based on non-unknown departments only
    const totalKnownDepartments = Object.values(departmentCount).reduce((sum, count) => sum + count, 0)
    
    const departmentData = Object.entries(departmentCount).map(([name, value]) => ({
      name,
      value,
      percentage: totalKnownDepartments > 0 ? ((value / totalKnownDepartments) * 100).toFixed(1) : 0
    }))

    // Status distribution using the enhanced status logic
    const statusCount = {}
    
    rolesData.forEach(role => {
      const status = getEmployeeStatus(role)
      statusCount[status] = (statusCount[status] || 0) + 1
    })

    const statusData = Object.entries(statusCount).map(([name, value]) => ({
      name,
      value,
      percentage: ((value / rolesData.length) * 100).toFixed(1)
    }))

    return { departmentData, statusData }
  }

  const { departmentData, statusData } = prepareChartData()

  // Different colors for each chart
  const departmentColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F']
  const statusColors = [  '#28a745','#dc3545','#ffc107', '#17a2b8', '#6f42c1', '#e83e8c', '#fd7e14', '#20c997']

  const DetailTable = ({ title, data, columns }) => (
    <Paper sx={{ bgcolor: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
      <Box sx={{ p: 3, bgcolor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}>
        <Typography variant="h6" color="#333" fontWeight="600">
          {title}
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 500 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              {columns.map((col, index) => (
                <TableCell key={index} sx={{ fontWeight: 600, color: "#666", fontSize: "0.9rem" }}>
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(data) && data.length > 0 ? (
              data.slice(0, 10).map((row, index) => (
                <TableRow key={index} hover sx={{ "&:hover": { bgcolor: "#f8f9fa" } }}>
                  {columns.map((col, colIndex) => (
                    <TableCell key={colIndex} sx={{ fontSize: "0.85rem", color: "#333", py: 2 }}>
                      {col.render ? col.render(row) : row[col.field] || "N/A"}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" color="#666">
                    No employee data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {Array.isArray(data) && data.length > 10 && (
        <Box sx={{ p: 2, textAlign: "center", bgcolor: "#f8f9fa", borderTop: "1px solid #e9ecef" }}>
          <Typography variant="body2" color="#666">
            Showing 10 of {data.length} employees
          </Typography>
        </Box>
      )}
    </Paper>
  )

  const ChartCard = ({ title, children }) => (
    <Card sx={{ height: '100%', borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="600" color="#333" sx={{ mb: 2 }}>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Charts Section - Only 2 charts now */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <ChartCard title="Department Distribution">
                {departmentData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={departmentData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                      >
                        {departmentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={departmentColors[index % departmentColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, 'Employees']} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography variant="body2" color="#666">
                      No department data available
                    </Typography>
                  </Box>
                )}
              </ChartCard>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <ChartCard title="Status Distribution">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={statusData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip formatter={(value) => [value, 'Employees']} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={statusColors[index % statusColors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>
        </Grid>

       
      </Grid>
    </Box>
  )
}

export default EmployeeSection